import { fetchCached } from '@/lib/api';

export const FALLBACK_ABOUT_CONTENT = {
  mission_title: 'Our Mission',
  mission_text: 'Shining the light of Christ among the nations, uniting people of all backgrounds into God\'s family, and serving with love as countless stars in His Kingdom.',
  vision_title: 'Our Vision',
  vision_text: 'Being an inclusive Ministry that preaches the Gospel to everyone, every community through activities that show God\'s love for humanity.',
  values_title: 'Our Core Values',
  values: [
    { title: 'Love', description: 'We are called to love one another as Christ loved us. Love is the foundation of all we do in our ministry and community outreach.' },
    { title: 'Compassion', description: 'We show Christ\'s mercy by caring for those in need, both spiritually and physically, extending a helping hand to all.' },
    { title: 'Fellowship', description: 'We build authentic community where every person belongs and grows in faith together, supporting one another through life\'s journey.' },
    { title: 'Integrity', description: 'We live transparently and honestly, honoring God in all our actions and decisions, holding ourselves to the highest moral standards.' },
    { title: 'Respect', description: 'We treat every person with dignity, recognizing that all are made in the image of God, regardless of background or circumstance.' },
    { title: 'Honesty', description: 'We commit to truthfulness in our words and actions, reflecting the character of Christ in every interaction.' },
    { title: 'Teamwork', description: 'We work together as the body of Christ, each contributing unique gifts for the common good of our ministry and community.' },
  ],
};

export const FALLBACK_FAITH_CONTENT = {
  title: 'Statement of Faith',
  intro: 'At Million Stars Community Ministry, our faith is grounded in the timeless truths of Scripture. We hold these beliefs as central to our identity and mission.',
  items: [
    { number: 1, title: 'The Word of God', text: 'We believe the Bible is the inspired Word of God, infallible and authoritative in all matters of faith and practice. It is the supreme source of truth for Christian beliefs and living, given by God through the Holy Spirit to guide, instruct, and transform His people.' },
    { number: 2, title: 'One God', text: 'We believe in one God, eternally existing in three persons: Father, Son, and Holy Spirit. These three are co-equal and co-eternal, united in purpose and nature, and together they reveal the fullness of God\'s character and love.' },
    { number: 3, title: 'Salvation Through Christ', text: 'We believe in salvation through Jesus Christ alone, who died for our sins, was buried, and rose again on the third day according to the Scriptures. Salvation is a gift of God, received through faith, not by works, so that no one may boast.' },
    { number: 4, title: 'Unity of Believers', text: 'We believe in the spiritual unity of believers in our Lord Jesus Christ. All who are born again of the Spirit are members of the Body of Christ, called to love, support, and serve one another as a testimony to the world.' },
    { number: 5, title: 'Marriage', text: 'We believe in marriage as a sacred covenant between one man and one woman, as instituted by God in the beginning and affirmed throughout Scripture. Marriage reflects the relationship between Christ and His Church.' },
  ],
};

export const FALLBACK_FOUNDER_CONTENT = {
  name: 'Rev. Pastor Jean Alexander Ovil',
  title: 'Founder & Senior Pastor',
  photo_url: '/founder.jpg',
  bio: 'Rev. Pastor Jean Alexander Ovil is the visionary founder and Senior Pastor of Million Stars Community Ministry. With a profound calling to unite people of all backgrounds into God\'s family, Pastor Ovil has dedicated his life to ministry, community service, and the proclamation of the Gospel.\n\nBorn with a deep passion for serving others, Pastor Ovil felt the call to ministry at a young age. His journey of faith led him through years of theological study and practical ministry experience, preparing him for the establishment of Million Stars Community Ministry.\n\nUnder his leadership, the ministry has grown into a vibrant, inclusive community that reaches across cultural and social boundaries. Pastor Ovil\'s vision of being \'countless stars in God\'s Kingdom\' has inspired many to discover their purpose and shine their light in the world.',
};

export const FALLBACK_CONTACT_INFO = {
  phone: '+1 (000) 000-0000',
  email: 'info@millionstarsministry.org',
  address: '',
  map_embed_url: '',
};

export const FALLBACK_ACTIVITIES_CONTENT = {
  title: '2027 Annual Activities Calendar',
  activities: [
    {
      icon: 'dinner',
      month: 'FEB',
      day: '27',
      title: 'Pastors and Wives Romantic Dinner',
      date: 'Saturday, February 27, 2027',
      detail: '',
    },
    {
      icon: 'banquet',
      month: 'MAY',
      day: '30',
      title: "Mothers and Daughters' Banquet",
      date: 'Saturday, May 30, 2027',
      detail: '',
    },
    {
      icon: 'camp',
      month: 'JUN',
      day: '',
      title: 'Happy Kids Summer Camp',
      date: 'June 2027',
      detail: '',
    },
    {
      icon: 'soccer',
      month: 'JUL',
      day: '',
      title: 'Interchurch Soccer League',
      date: 'July 2027',
      detail: '',
    },
    {
      icon: 'youth',
      month: 'SEP',
      day: '',
      title: 'International Youth Conference',
      date: 'September 2027',
      detail: '',
    },
    {
      icon: 'bible',
      month: 'OCT',
      day: '',
      title: 'Interchurch Bible Contest / Concours Biblique Interéglise',
      date: 'October 2027',
      detail: '',
    },
    {
      icon: 'music',
      month: 'DEC',
      day: '',
      title: 'Konkou Chante Nwèl 2027',
      date: 'December 2027',
      registration_period: 'January to June 2027',
      big_final: 'December 2027',
      location: 'Tampa, Florida, USA',
      detail: 'Registration Period: January to June 2027. Big Final: December 2027. Location: Tampa, Florida, USA.',
    },
  ],
};

const ROUTE_PREFETCHERS = {
  '/': () => Promise.all([
    fetchCached('/content/home'),
    fetchCached('/content/site_settings'),
    fetchCached('/donations/settings'),
  ]),
  '/about': () => fetchCached('/content/about'),
  '/statement-of-faith': () => fetchCached('/content/faith'),
  '/founder': () => fetchCached('/content/founder'),
  '/activities': () => fetchCached('/content/activities'),
  '/gallery': () => fetchCached('/gallery', { ttlMs: 2 * 60 * 1000 }),
  '/blog': () => fetchCached('/blog?page=1&limit=9', { ttlMs: 2 * 60 * 1000 }),
  '/contact': () => fetchCached('/content/contact_info'),
};

const prefetchedRoutes = new Set();

export const prefetchPublicRoute = (path) => {
  const prefetcher = ROUTE_PREFETCHERS[path];
  if (!prefetcher || prefetchedRoutes.has(path)) return;
  prefetchedRoutes.add(path);
  prefetcher().catch(() => {
    prefetchedRoutes.delete(path);
  });
};

export const warmPublicRouteCache = () => {
  Object.keys(ROUTE_PREFETCHERS).forEach((path) => {
    prefetchPublicRoute(path);
  });
};
